import {
    AlertTriangle,
    Building2,
    ChevronDown,
    ChevronUp,
    Landmark,
    LoaderCircle,
    Save,
    ShieldCheck,
    Sparkles,
    Table,
    Wallet,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { toast } from 'react-toastify'
import { useLocation, useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ComplianceConfigScreen.module.css'

const INDIAN_STATE_OPTIONS = [
    { code: 'AN', name: 'Andaman and Nicobar Islands' },
    { code: 'AP', name: 'Andhra Pradesh' },
    { code: 'AR', name: 'Arunachal Pradesh' },
    { code: 'AS', name: 'Assam' },
    { code: 'BR', name: 'Bihar' },
    { code: 'CH', name: 'Chandigarh' },
    { code: 'CG', name: 'Chhattisgarh' },
    { code: 'DD', name: 'Daman and Diu' },
    { code: 'DL', name: 'Delhi' },
    { code: 'DN', name: 'Dadra and Nagar Haveli' },
    { code: 'GA', name: 'Goa' },
    { code: 'GJ', name: 'Gujarat' },
    { code: 'HP', name: 'Himachal Pradesh' },
    { code: 'HR', name: 'Haryana' },
    { code: 'JH', name: 'Jharkhand' },
    { code: 'JK', name: 'Jammu and Kashmir' },
    { code: 'KA', name: 'Karnataka' },
    { code: 'KL', name: 'Kerala' },
    { code: 'LA', name: 'Ladakh' },
    { code: 'LD', name: 'Lakshadweep' },
    { code: 'MH', name: 'Maharashtra' },
    { code: 'ML', name: 'Meghalaya' },
    { code: 'MN', name: 'Manipur' },
    { code: 'MP', name: 'Madhya Pradesh' },
    { code: 'MZ', name: 'Mizoram' },
    { code: 'NL', name: 'Nagaland' },
    { code: 'OD', name: 'Odisha' },
    { code: 'PB', name: 'Punjab' },
    { code: 'PY', name: 'Puducherry' },
    { code: 'RJ', name: 'Rajasthan' },
    { code: 'SK', name: 'Sikkim' },
    { code: 'TN', name: 'Tamil Nadu' },
    { code: 'TG', name: 'Telangana' },
    { code: 'TR', name: 'Tripura' },
    { code: 'UP', name: 'Uttar Pradesh' },
    { code: 'UK', name: 'Uttarakhand' },
    { code: 'WB', name: 'West Bengal' },
]

const PT_APPLICABLE_CODES = new Set(['MH', 'KA', 'WB', 'TN', 'AP', 'TG', 'GJ', 'MP', 'OD', 'AS', 'HP', 'MN', 'MZ', 'NL', 'SK', 'TR'])
const WAGE_CATEGORIES = ['UNSKILLED', 'SEMI_SKILLED', 'SKILLED', 'HIGHLY_SKILLED']

const getTodayDate = () => {
    const now = new Date()
    const tzFixed = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    return tzFixed.toISOString().slice(0, 10)
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const DEFAULT_FORM = {
    stateCode: '',
    isPfRegistered: false,
    pfRegistrationNumber: '',
    pfEmployeePercent: '12',
    pfEmployerPercent: '12',
    pfBasicCap: '15000',
    isEsiRegistered: false,
    esiRegistrationNumber: '',
    esiEmployeePercent: '0.75',
    esiEmployerPercent: '3.25',
    esiGrossLimit: '21000',
    isProfessionalTaxApplicable: false,
    minimumWageCategory: 'SEMI_SKILLED',
    minimumWageAmount: '0',
    minimumWageLastUpdated: getTodayDate(),
    isGratuityTracked: true,
    tdsThresholdAnnual: '250000',
}

const normalizeFormForCompare = (form) => ({
    stateCode: String(form?.stateCode || ''),
    isPfRegistered: Boolean(form?.isPfRegistered),
    pfRegistrationNumber: String(form?.pfRegistrationNumber || '').trim(),
    pfEmployeePercent: String(form?.pfEmployeePercent || ''),
    pfEmployerPercent: String(form?.pfEmployerPercent || ''),
    pfBasicCap: String(form?.pfBasicCap || ''),
    isEsiRegistered: Boolean(form?.isEsiRegistered),
    esiRegistrationNumber: String(form?.esiRegistrationNumber || '').trim(),
    esiEmployeePercent: String(form?.esiEmployeePercent || ''),
    esiEmployerPercent: String(form?.esiEmployerPercent || ''),
    esiGrossLimit: String(form?.esiGrossLimit || ''),
    isProfessionalTaxApplicable: Boolean(form?.isProfessionalTaxApplicable),
    minimumWageCategory: String(form?.minimumWageCategory || ''),
    minimumWageAmount: String(form?.minimumWageAmount || ''),
    minimumWageLastUpdated: String(form?.minimumWageLastUpdated || ''),
    isGratuityTracked: Boolean(form?.isGratuityTracked),
    tdsThresholdAnnual: String(form?.tdsThresholdAnnual || ''),
})

const SectionHeader = ({ title, subtitle, icon: Icon, expanded, onToggle, alwaysOpen = false }) => (
    <button type="button" className={styles.sectionHeader} onClick={onToggle} disabled={alwaysOpen}>
        <div className={styles.sectionTitleWrap}>
            <span className={styles.sectionIcon}><Icon size={15} /></span>
            <div>
                <h3>{title}</h3>
                {subtitle && <p>{subtitle}</p>}
            </div>
        </div>
        {alwaysOpen ? null : (expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
    </button>
)

const formatInr = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const ComplianceConfigScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''
    const incomingConfigIdentifier = location?.state?.configIdentifierParam
        || location?.state?.existingConfigIdentifier
        || null

    const [form, setForm] = useState(DEFAULT_FORM)
    const [expanded, setExpanded] = useState({ basic: true, pf: false, esi: false, pt: false, wage: true })
    const [fieldErrors, setFieldErrors] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [ptData, setPtData] = useState(null)
    const [isFetchingPT, setIsFetchingPT] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [isEditMode, setIsEditMode] = useState(Boolean(incomingConfigIdentifier))
    const [configIdentifier, setConfigIdentifier] = useState(incomingConfigIdentifier)
    const [initialFormSnapshot, setInitialFormSnapshot] = useState(normalizeFormForCompare(DEFAULT_FORM))

    const selectedState = useMemo(() => INDIAN_STATE_OPTIONS.find((item) => item.code === form.stateCode), [form.stateCode])

    const hasUnsavedChanges = useMemo(() => {
        const current = normalizeFormForCompare(form)
        return JSON.stringify(current) !== JSON.stringify(initialFormSnapshot)
    }, [form, initialFormSnapshot])

    const goBackToSetup = useCallback(({ skipUnsavedGuard = false } = {}) => {
        if (!skipUnsavedGuard && hasUnsavedChanges) {
            const shouldLeave = window.confirm('You have unsaved changes. Leave without saving?')
            if (!shouldLeave) return
        }
        if (window.history.length > 1) {
            navigate(-1)
            return
        }
        navigate('/payroll/setup', { replace: true })
    }, [hasUnsavedChanges, navigate])

    const statePtHint = useMemo(() => {
        if (!form.stateCode) return ''
        if (PT_APPLICABLE_CODES.has(form.stateCode)) {
            return `Professional Tax applies in ${selectedState?.name || form.stateCode}. Enable in Section 4.`
        }
        return `Professional Tax does not apply in ${selectedState?.name || form.stateCode}.`
    }, [form.stateCode, selectedState?.name])

    const fetchStatePTDefaults = useCallback(async (selectedStateCode) => {
        if (!authCtx?.token || !storeIdentifier || !selectedStateCode) return

        try {
            setIsFetchingPT(true)
            setPtData(null)

            const response = await fetch(BASE_URL_API + 'getStatePTDefaults', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    stateCode: selectedStateCode,
                }),
            })

            if (!response.ok) {
                console.warn('getStatePTDefaults: network error')
                return
            }

            const result = await response.json()

            if (result?.status === 'success') {
                const responseData = result?.response || null
                setPtData(responseData)

                setForm((prev) => ({
                    ...prev,
                    isProfessionalTaxApplicable: responseData?.isPTApplicable
                        ? prev.isProfessionalTaxApplicable
                        : false,
                }))
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            console.warn('getStatePTDefaults error:', result?.message)
        } catch (error) {
            console.error('fetchStatePTDefaults error:', error)
        } finally {
            setIsFetchingPT(false)
        }
    }, [authCtx, storeIdentifier])

    const handleStateChange = (selectedStateCode) => {
        updateField('stateCode', selectedStateCode)
        fetchStatePTDefaults(selectedStateCode)
    }

    const updateField = (key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value }
            if (key === 'minimumWageAmount' && String(value) !== String(prev.minimumWageAmount)) {
                next.minimumWageLastUpdated = getTodayDate()
            }
            return next
        })
        setFieldErrors((prev) => ({ ...prev, [key]: '' }))
    }

    const toggleSection = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

    const onTogglePf = (checked) => {
        setForm((prev) => ({
            ...prev,
            isPfRegistered: checked,
            pfRegistrationNumber: checked ? prev.pfRegistrationNumber : '',
            pfEmployeePercent: checked ? (prev.pfEmployeePercent || '12') : prev.pfEmployeePercent,
            pfEmployerPercent: checked ? (prev.pfEmployerPercent || '12') : prev.pfEmployerPercent,
            pfBasicCap: checked ? (prev.pfBasicCap || '15000') : prev.pfBasicCap,
        }))
        setExpanded((prev) => ({ ...prev, pf: checked }))
        setFieldErrors((prev) => ({ ...prev, pfRegistrationNumber: '' }))
    }

    const onToggleEsi = (checked) => {
        setForm((prev) => ({
            ...prev,
            isEsiRegistered: checked,
            esiRegistrationNumber: checked ? prev.esiRegistrationNumber : '',
            esiEmployeePercent: checked ? (prev.esiEmployeePercent || '0.75') : prev.esiEmployeePercent,
            esiEmployerPercent: checked ? (prev.esiEmployerPercent || '3.25') : prev.esiEmployerPercent,
            esiGrossLimit: checked ? (prev.esiGrossLimit || '21000') : prev.esiGrossLimit,
        }))
        setExpanded((prev) => ({ ...prev, esi: checked }))
        setFieldErrors((prev) => ({ ...prev, esiRegistrationNumber: '' }))
    }

    const validate = () => {
        const nextErrors = {}

        if (!form.stateCode) nextErrors.stateCode = 'Please select a valid state.'
        if (form.isPfRegistered && !String(form.pfRegistrationNumber || '').trim()) {
            nextErrors.pfRegistrationNumber = 'PF registration number is required when PF is enabled.'
        }
        if (form.isEsiRegistered && !String(form.esiRegistrationNumber || '').trim()) {
            nextErrors.esiRegistrationNumber = 'ESI registration number is required when ESI is enabled.'
        }

        const toNum = (value) => Number(value || 0)
        const percentFields = [
            ['pfEmployeePercent', toNum(form.pfEmployeePercent)],
            ['pfEmployerPercent', toNum(form.pfEmployerPercent)],
            ['esiEmployeePercent', toNum(form.esiEmployeePercent)],
            ['esiEmployerPercent', toNum(form.esiEmployerPercent)],
        ]

        percentFields.forEach(([key, val]) => {
            if (!Number.isFinite(val) || val < 0 || val > 100) nextErrors[key] = 'Must be between 0 and 100.'
        })

        const amountFields = [
            ['pfBasicCap', toNum(form.pfBasicCap)],
            ['esiGrossLimit', toNum(form.esiGrossLimit)],
            ['minimumWageAmount', toNum(form.minimumWageAmount)],
            ['tdsThresholdAnnual', toNum(form.tdsThresholdAnnual)],
        ]

        amountFields.forEach(([key, val]) => {
            if (!Number.isFinite(val) || val < 0) nextErrors[key] = 'Amount cannot be negative.'
        })

        if (!WAGE_CATEGORIES.includes(form.minimumWageCategory)) {
            nextErrors.minimumWageCategory = 'Please select a valid minimum wage category.'
        }
        if (!form.minimumWageLastUpdated) nextErrors.minimumWageLastUpdated = 'Please select the last updated date.'

        setFieldErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const buildPayload = useCallback((overrideConfigIdentifier = '') => {
        const payload = {
            token: authCtx?.token,
            storeIdentifier,
            stateCode: form.stateCode,
            isPfRegistered: form.isPfRegistered ? 1 : 0,
            pfEmployeePercent: Number(form.pfEmployeePercent || 0),
            pfEmployerPercent: Number(form.pfEmployerPercent || 0),
            pfBasicCap: Number(form.pfBasicCap || 0),
            isEsiRegistered: form.isEsiRegistered ? 1 : 0,
            esiEmployeePercent: Number(form.esiEmployeePercent || 0),
            esiEmployerPercent: Number(form.esiEmployerPercent || 0),
            esiGrossLimit: Number(form.esiGrossLimit || 0),
            isProfessionalTaxApplicable: form.isProfessionalTaxApplicable ? 1 : 0,
            minimumWageCategory: form.minimumWageCategory,
            minimumWageAmount: Number(form.minimumWageAmount || 0),
            minimumWageLastUpdated: form.minimumWageLastUpdated || getTodayDate(),
            isGratuityTracked: form.isGratuityTracked ? 1 : 0,
            tdsThresholdAnnual: Number(form.tdsThresholdAnnual || 0),
        }

        if (form.isPfRegistered) payload.pfRegistrationNumber = String(form.pfRegistrationNumber || '').trim()
        if (form.isEsiRegistered) payload.esiRegistrationNumber = String(form.esiRegistrationNumber || '').trim()

        const identifier = overrideConfigIdentifier || configIdentifier
        if (identifier) payload.configIdentifier = identifier
        return payload
    }, [authCtx?.token, configIdentifier, form, storeIdentifier])

    const mapResponseToForm = useCallback((responseData) => {
        if (!responseData) return
        const mapped = {
            stateCode: responseData?.stateCode || '',
            isPfRegistered: Number(responseData?.isPfRegistered || 0) === 1,
            pfRegistrationNumber: responseData?.pfRegistrationNumber || '',
            pfEmployeePercent: String(responseData?.pfEmployeePercent ?? 12),
            pfEmployerPercent: String(responseData?.pfEmployerPercent ?? 12),
            pfBasicCap: String(responseData?.pfBasicCap ?? 15000),
            isEsiRegistered: Number(responseData?.isEsiRegistered || 0) === 1,
            esiRegistrationNumber: responseData?.esiRegistrationNumber || '',
            esiEmployeePercent: String(responseData?.esiEmployeePercent ?? 0.75),
            esiEmployerPercent: String(responseData?.esiEmployerPercent ?? 3.25),
            esiGrossLimit: String(responseData?.esiGrossLimit ?? 21000),
            isProfessionalTaxApplicable: Number(responseData?.isProfessionalTaxApplicable || 0) === 1,
            minimumWageCategory: responseData?.minimumWageCategory || 'SEMI_SKILLED',
            minimumWageAmount: String(responseData?.minimumWageAmount ?? 0),
            minimumWageLastUpdated: responseData?.minimumWageLastUpdated || getTodayDate(),
            isGratuityTracked: Number(responseData?.isGratuityTracked ?? 1) === 1,
            tdsThresholdAnnual: String(responseData?.tdsThresholdAnnual ?? 250000),
        }
        setForm(mapped)
        setExpanded((prev) => ({
            ...prev,
            pf: mapped.isPfRegistered,
            esi: mapped.isEsiRegistered,
        }))
        setInitialFormSnapshot(normalizeFormForCompare(mapped))
    }, [])

    const loadExistingConfig = useCallback(async ({ silent = false, forcedConfigIdentifier = null } = {}) => {
        if (!storeIdentifier) {
            setIsLoading(false)
            return null
        }
        if (!silent) setIsLoading(true)
        try {
            const response = await fetch(BASE_URL_API + 'getStoreComplianceConfig', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    ...(forcedConfigIdentifier || incomingConfigIdentifier
                        ? { configIdentifier: forcedConfigIdentifier || incomingConfigIdentifier }
                        : {}),
                }),
            })

            if (!response.ok) {
                if (!silent) setIsLoading(false)
                return null
            }

            const result = await response.json()

            if (result?.status === 'success') {
                const config = result?.response?.config || result?.response || null
                if (config?.configIdentifier) {
                    setConfigIdentifier(config.configIdentifier)
                    setIsEditMode(true)
                    mapResponseToForm(config)
                    if (config?.stateCode) {
                        fetchStatePTDefaults(config.stateCode)
                    }
                }
                return config
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }

            if (incomingConfigIdentifier) {
                toast.error('Could not load compliance settings.')
            }
            return null
        } catch {
            if (incomingConfigIdentifier) {
                toast.error('Network error while loading config.')
            }
            return null
        } finally {
            if (!silent) setIsLoading(false)
        }
    }, [authCtx, fetchStatePTDefaults, incomingConfigIdentifier, mapResponseToForm, storeIdentifier])

    useEffect(() => {
        loadExistingConfig()
    }, [loadExistingConfig])

    useEffect(() => {
        const onBeforeUnload = (event) => {
            if (!hasUnsavedChanges) return
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [hasUnsavedChanges])

    const applyApiError = (result, fallbackMessage) => {
        if (result?.errorCode === 'SYS_002') {
            setErrorMessage('Server error. Please try again in a moment.')
            return
        }
        setErrorMessage(result?.message || fallbackMessage)
    }

    const ensureValidBeforeSave = () => {
        if (!storeIdentifier) {
            setErrorMessage('Please select an active store first.')
            return false
        }
        if (!validate()) return false
        return true
    }

    const updateComplianceConfig = async ({ overrideConfigIdentifier = null } = {}) => {
        const resolvedIdentifier = overrideConfigIdentifier || configIdentifier
        if (!ensureValidBeforeSave() || !resolvedIdentifier) {
            if (!resolvedIdentifier) {
                setErrorMessage('storeIdentifier and configIdentifier are required.')
            }
            return
        }

        setIsSaving(true)
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'updateStoreComplianceConfig', {
                method: 'POST',
                body: JSON.stringify(buildPayload(resolvedIdentifier)),
            })

            if (!response.ok) {
                setErrorMessage('Something went wrong. Please try again.')
                return
            }

            const result = await response.json()

            if (result?.status === 'success') {
                toast.success('Compliance settings updated successfully.')
                const config = result?.response || null
                if (config?.configIdentifier) setConfigIdentifier(config.configIdentifier)
                if (config) mapResponseToForm(config)
                goBackToSetup({ skipUnsavedGuard: true })
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            applyApiError(result, 'Failed to update. Please try again.')
        } catch {
            setErrorMessage('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const saveComplianceConfig = async () => {
        if (!ensureValidBeforeSave()) return

        setIsSaving(true)
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'createStoreComplianceConfig', {
                method: 'POST',
                body: JSON.stringify(buildPayload()),
            })

            if (!response.ok) {
                setErrorMessage('Something went wrong. Please try again.')
                return
            }

            const result = await response.json()

            if (result?.status === 'success') {
                const createdIdentifier = result?.response?.configIdentifier || null
                if (createdIdentifier) {
                    setConfigIdentifier(createdIdentifier)
                    setIsEditMode(true)
                }
                toast.success('Compliance settings saved successfully.')
                if (result?.response) mapResponseToForm(result.response)
                goBackToSetup({ skipUnsavedGuard: true })
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (result?.errorCode === 'VAL_004') {
                const existingIdentifier = result?.response?.existingConfigIdentifier || null
                if (existingIdentifier) {
                    setConfigIdentifier(existingIdentifier)
                    setIsEditMode(true)
                    await updateComplianceConfig({ overrideConfigIdentifier: existingIdentifier })
                    return
                }
            }

            applyApiError(result, 'Failed to save. Please try again.')
        } catch {
            setErrorMessage('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const handleSave = async (event) => {
        event.preventDefault()
        if (isLoading) return
        if (isEditMode) {
            await updateComplianceConfig()
            return
        }
        await saveComplianceConfig()
    }

    const renderPTSection = () => {
        if (!form.stateCode) return null

        if (isFetchingPT) {
            return <p className={styles.ptHint}>Loading PT rates...</p>
        }

        if (!ptData) return null

        if (!ptData?.isPTApplicable) {
            return (
                <div className={styles.ptNotApplicableBox}>
                    <p className={styles.ptNotApplicableText}>
                        ✓ Professional Tax does not apply in {ptData?.stateName || selectedState?.name || form.stateCode}. No PT will be deducted from staff salaries.
                    </p>
                </div>
            )
        }

        if (!form.isProfessionalTaxApplicable || !Array.isArray(ptData?.slabs)) {
            return null
        }

        return (
            <div className={styles.ptSlabTable}>
                <p className={styles.ptSlabTitle}>
                    <Table size={14} /> PT Slabs — {ptData?.stateName || form.stateCode} (Platform Default)
                </p>

                <div className={`${styles.ptSlabRow} ${styles.ptSlabHeaderRow}`}>
                    <span className={styles.ptSlabHeader}>Gross From</span>
                    <span className={styles.ptSlabHeader}>Gross To</span>
                    <span className={styles.ptSlabHeader}>Monthly PT</span>
                </div>

                {ptData.slabs.map((slab, index) => (
                    <div key={`pt-slab-${index}`} className={styles.ptSlabRow}>
                        <span className={styles.ptSlabCell}>{formatInr(slab?.minSalary)}</span>
                        <span className={styles.ptSlabCell}>{slab?.maxSalary !== null ? formatInr(slab?.maxSalary) : 'No limit'}</span>
                        <span className={`${styles.ptSlabCell} ${styles.ptAmount}`}>{Number(slab?.monthlyPT || 0) === 0 ? '—' : formatInr(slab?.monthlyPT)}</span>
                    </div>
                ))}

                {ptData?.notes && <p className={styles.ptNotes}>{ptData.notes}</p>}

                <p className={styles.ptDisclaimer}>
                    These are platform defaults. PT is applied automatically during payroll computation based on each staff member's gross salary.
                </p>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroPill}><Sparkles size={14} /> Statutory Compliance Setup</div>
                <h1>Configure PF, ESI, PT, and wage compliance in one flow.</h1>
                <p>Keep this setup accurate once and payroll will stay reliable each cycle.</p>
            </section>

            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select a store to continue.</strong>
                        <p>Compliance setup is store-specific and requires an active store.</p>
                    </div>
                </section>
            )}

            {errorMessage && (
                <section className={`${styles.alertCard} ${styles.errorCard}`}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Unable to save compliance config.</strong>
                        <p>{errorMessage}</p>
                    </div>
                </section>
            )}

            {isLoading && (
                <section className={styles.alertCard}>
                    <LoaderCircle size={16} className={styles.spin} />
                    <div>
                        <strong>Loading compliance settings...</strong>
                        <p>Preparing your form with current values.</p>
                    </div>
                </section>
            )}

            <form className={styles.formCard} onSubmit={handleSave}>
                <fieldset disabled={isLoading} style={{ all: 'unset', display: 'contents' }}>
                <section className={styles.sectionCard}>
                    <SectionHeader title="1. Basic Info" subtitle="Required for location-specific compliance mapping" icon={Building2} expanded={true} onToggle={() => undefined} alwaysOpen />
                    <div className={styles.sectionBody}>
                        <div className={styles.fieldRowSingle}>
                            <label htmlFor="stateCode">State <span className={styles.required}>*</span></label>
                            <select id="stateCode" value={form.stateCode} onChange={(event) => handleStateChange(event.target.value)}>
                                <option value="">Select state</option>
                                {INDIAN_STATE_OPTIONS.map((state) => (
                                    <option key={state.code} value={state.code}>{state.name} ({state.code})</option>
                                ))}
                            </select>
                            {fieldErrors?.stateCode && <span className={styles.fieldError}>{fieldErrors.stateCode}</span>}
                        </div>

                        <div className={styles.infoHint}>Store is located in: <strong>{selectedState?.name || '—'}</strong></div>
                        {statePtHint && <div className={styles.infoHintSoft}>{statePtHint}</div>}
                    </div>
                </section>

                <section className={styles.sectionCard}>
                    <SectionHeader title="2. Provident Fund (PF)" subtitle="Enable only if your salon is PF-registered" icon={ShieldCheck} expanded={expanded.pf} onToggle={() => toggleSection('pf')} />
                    <div className={styles.sectionBody}>
                        <label className={styles.toggleRow}>
                            <input type="checkbox" checked={form.isPfRegistered} onChange={(event) => onTogglePf(event.target.checked)} />
                            <span>PF Registration Enabled</span>
                        </label>

                        {expanded.pf && (
                            <div className={styles.gridTwo}>
                                <div className={styles.fieldWrap}>
                                    <label htmlFor="pfRegistrationNumber">PF Registration Number {form.isPfRegistered ? <span className={styles.required}>*</span> : null}</label>
                                    <input id="pfRegistrationNumber" type="text" value={form.pfRegistrationNumber} onChange={(event) => updateField('pfRegistrationNumber', event.target.value)} placeholder="Enter PF registration" />
                                    {fieldErrors?.pfRegistrationNumber && <span className={styles.fieldError}>{fieldErrors.pfRegistrationNumber}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="pfEmployeePercent">Employee Contribution (%)</label>
                                    <input id="pfEmployeePercent" type="number" step="0.01" min="0" max="100" value={form.pfEmployeePercent} onChange={(event) => updateField('pfEmployeePercent', event.target.value)} />
                                    {fieldErrors?.pfEmployeePercent && <span className={styles.fieldError}>{fieldErrors.pfEmployeePercent}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="pfEmployerPercent">Employer Contribution (%)</label>
                                    <input id="pfEmployerPercent" type="number" step="0.01" min="0" max="100" value={form.pfEmployerPercent} onChange={(event) => updateField('pfEmployerPercent', event.target.value)} />
                                    {fieldErrors?.pfEmployerPercent && <span className={styles.fieldError}>{fieldErrors.pfEmployerPercent}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="pfBasicCap">PF Computation Cap (₹)</label>
                                    <input id="pfBasicCap" type="number" step="0.01" min="0" value={form.pfBasicCap} onChange={(event) => updateField('pfBasicCap', event.target.value)} />
                                    {fieldErrors?.pfBasicCap && <span className={styles.fieldError}>{fieldErrors.pfBasicCap}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className={styles.sectionCard}>
                    <SectionHeader title="3. ESI (Employee State Insurance)" subtitle="Enable only if your salon is ESI-registered" icon={Landmark} expanded={expanded.esi} onToggle={() => toggleSection('esi')} />
                    <div className={styles.sectionBody}>
                        <label className={styles.toggleRow}>
                            <input type="checkbox" checked={form.isEsiRegistered} onChange={(event) => onToggleEsi(event.target.checked)} />
                            <span>ESI Registration Enabled</span>
                        </label>

                        {expanded.esi && (
                            <div className={styles.gridTwo}>
                                <div className={styles.fieldWrap}>
                                    <label htmlFor="esiRegistrationNumber">ESI Registration Number {form.isEsiRegistered ? <span className={styles.required}>*</span> : null}</label>
                                    <input id="esiRegistrationNumber" type="text" value={form.esiRegistrationNumber} onChange={(event) => updateField('esiRegistrationNumber', event.target.value)} placeholder="Enter ESI registration" />
                                    {fieldErrors?.esiRegistrationNumber && <span className={styles.fieldError}>{fieldErrors.esiRegistrationNumber}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="esiEmployeePercent">Employee Contribution (%)</label>
                                    <input id="esiEmployeePercent" type="number" step="0.01" min="0" max="100" value={form.esiEmployeePercent} onChange={(event) => updateField('esiEmployeePercent', event.target.value)} />
                                    {fieldErrors?.esiEmployeePercent && <span className={styles.fieldError}>{fieldErrors.esiEmployeePercent}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="esiEmployerPercent">Employer Contribution (%)</label>
                                    <input id="esiEmployerPercent" type="number" step="0.01" min="0" max="100" value={form.esiEmployerPercent} onChange={(event) => updateField('esiEmployerPercent', event.target.value)} />
                                    {fieldErrors?.esiEmployerPercent && <span className={styles.fieldError}>{fieldErrors.esiEmployerPercent}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="esiGrossLimit">Gross Salary Limit (₹)</label>
                                    <input id="esiGrossLimit" type="number" step="0.01" min="0" value={form.esiGrossLimit} onChange={(event) => updateField('esiGrossLimit', event.target.value)} />
                                    {fieldErrors?.esiGrossLimit && <span className={styles.fieldError}>{fieldErrors.esiGrossLimit}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className={styles.sectionCard}>
                    <SectionHeader title="4. Professional Tax" subtitle="Enable when state PT is applicable" icon={Wallet} expanded={expanded.pt} onToggle={() => toggleSection('pt')} />
                    <div className={styles.sectionBody}>
                        <label className={styles.toggleRow}>
                            <input
                                type="checkbox"
                                checked={form.isProfessionalTaxApplicable}
                                onChange={(event) => {
                                    if (ptData && !ptData?.isPTApplicable) return
                                    updateField('isProfessionalTaxApplicable', event.target.checked)
                                }}
                                disabled={isFetchingPT || !form.stateCode || (ptData && !ptData?.isPTApplicable)}
                            />
                            <span>Professional Tax Applicable</span>
                        </label>
                        <p className={styles.staticHint}>PT applies in MH, KA, WB, TN and others. Does NOT apply in DL, RJ, HR, UP.</p>
                        {ptData && !ptData?.isPTApplicable && (
                            <p className={styles.ptDisabledHint}>PT is not applicable in {ptData?.stateName || selectedState?.name || form.stateCode}.</p>
                        )}
                        {renderPTSection()}
                    </div>
                </section>

                <section className={styles.sectionCard}>
                    <SectionHeader title="5. Minimum Wage & Compliance" subtitle="Set wage baseline and tax/gratuity controls" icon={ShieldCheck} expanded={expanded.wage} onToggle={() => toggleSection('wage')} />
                    {expanded.wage && (
                        <div className={styles.sectionBody}>
                            <div className={styles.gridTwo}>
                                <div className={styles.fieldWrap}>
                                    <label htmlFor="minimumWageCategory">Minimum Wage Category</label>
                                    <select id="minimumWageCategory" value={form.minimumWageCategory} onChange={(event) => updateField('minimumWageCategory', event.target.value)}>
                                        {WAGE_CATEGORIES.map((category) => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                    {fieldErrors?.minimumWageCategory && <span className={styles.fieldError}>{fieldErrors.minimumWageCategory}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="minimumWageAmount">Monthly Minimum Wage (₹)</label>
                                    <input id="minimumWageAmount" type="number" step="0.01" min="0" value={form.minimumWageAmount} onChange={(event) => updateField('minimumWageAmount', event.target.value)} />
                                    <span className={styles.helperText}>Check your state's latest minimum wage notification. Beauty/salon workers typically fall under Scheduled Employment rates.</span>
                                    {Number(form.minimumWageAmount || 0) <= 0 && <span className={styles.warningText}><AlertTriangle size={13} /> Minimum wage not set. System cannot flag below-minimum-wage payslips.</span>}
                                    {fieldErrors?.minimumWageAmount && <span className={styles.fieldError}>{fieldErrors.minimumWageAmount}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="minimumWageLastUpdated">Last Updated Date</label>
                                    <input id="minimumWageLastUpdated" type="date" value={form.minimumWageLastUpdated} onChange={(event) => updateField('minimumWageLastUpdated', event.target.value)} />
                                    {fieldErrors?.minimumWageLastUpdated && <span className={styles.fieldError}>{fieldErrors.minimumWageLastUpdated}</span>}
                                </div>

                                <div className={styles.fieldWrap}>
                                    <label htmlFor="tdsThresholdAnnual">TDS Threshold (Annual ₹)</label>
                                    <input id="tdsThresholdAnnual" type="number" step="0.01" min="0" value={form.tdsThresholdAnnual} onChange={(event) => updateField('tdsThresholdAnnual', event.target.value)} />
                                    {fieldErrors?.tdsThresholdAnnual && <span className={styles.fieldError}>{fieldErrors.tdsThresholdAnnual}</span>}
                                </div>
                            </div>

                            <label className={styles.toggleRow}>
                                <input type="checkbox" checked={form.isGratuityTracked} onChange={(event) => updateField('isGratuityTracked', event.target.checked)} />
                                <span>Track gratuity eligibility</span>
                            </label>
                        </div>
                    )}
                </section>

                <div className={styles.footerBar}>
                    <button type="button" className={styles.secondaryBtn} onClick={goBackToSetup}>Back</button>
                    <button type="submit" className={styles.primaryBtn} disabled={isSaving || isLoading || !storeIdentifier}>
                        {isSaving ? <LoaderCircle size={15} className={styles.spin} /> : <Save size={15} />} {isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save & Continue')} <FaArrowRightLong size={13} />
                    </button>
                </div>
                </fieldset>
            </form>
        </div>
    )
}

export default ComplianceConfigScreen